import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { convertDecimals } from '../../calculation/util';
import Table from '../../shared/component/Table';
import { ERole, EStableSlopeValidationType } from '../../shared/enum';
import { IResult } from '../../shared/iResult';
import variable from '../../shared/style/variable';
import { useAppSelector } from '../../store/hook';
import { selectResult } from '../../store/result/resultSlice';
import {
    getDrainageCapacityTable,
    getDrainageSystemTable,
    getDrainingDrainPipesTable,
    getGeneralPressuresInTheSoilTable,
    getInterventionSlopeValidationTable,
    getLongTermPressuresInTheSoilTable,
    getShortTermPressuresInTheSoilTable,
    getTransitionalRegimeTable,
    getWheelBaseTable,
} from './util';
import CheckboxCustom from '../../shared/component/Checkbox';
import { selectUser } from '../../store/user/userSlice';
import { useDispatch } from 'react-redux';
import { selectUtil, setUtil } from '../../store/util/utilSlice';

interface IOutputProps {}

const Output = (props: IOutputProps) => {
    const result = useAppSelector(selectResult);
    const user = useAppSelector(selectUser);
    const util = useAppSelector(selectUtil);

    const dispatch = useDispatch();

    let output: IResult = convertDecimals(result.data!);

    const { t } = useTranslation();

    let preInterventionSlopeValidationTable = getInterventionSlopeValidationTable(output.phaseOneOutputCalculation, EStableSlopeValidationType.Pre);
    let postInterventionSlopeValidationTable = getInterventionSlopeValidationTable(
        output.phaseTwoOutputCalculation.fsTablesWithResults.evaluationOfSlopeStabilityPostIntervention,
        EStableSlopeValidationType.Post,
    );
    let drainageCapacityTable = getDrainageCapacityTable(result.data!.phaseTwoOutputCalculation.drainingCapacity); //different decimal numbers
    let drainingDrainPipesTable = getDrainingDrainPipesTable(output.phaseTwoOutputCalculation.drainingDrainPipes);
    let wheelbaseTable = getWheelBaseTable(output.phaseTwoOutputCalculation.wheelbase);
    let transitionalRegimeTable = getTransitionalRegimeTable(output.phaseTwoOutputCalculation.transitionalRegime);
    let drainageSystemTable = getDrainageSystemTable(output.phaseTwoOutputCalculation.transitionalRegime.drainageSystem, output.phaseTwoOutputCalculation.transitionalRegime);
    let generalPressuresInTheSoilTable = getGeneralPressuresInTheSoilTable(output.phaseTwoOutputCalculation.calculationOfPressuresInTheSoil);
    let longTermPressuresInTheSoilTable = getLongTermPressuresInTheSoilTable(output.phaseTwoOutputCalculation.calculationOfPressuresInTheSoil.longTermDrain);
    let shortTermPressuresInTheSoilTable = getShortTermPressuresInTheSoilTable(output.phaseTwoOutputCalculation.calculationOfPressuresInTheSoil.shortTermDrain);

    return (
        <Stack variant='scrollable' style={{ overflowX: 'auto' }}>
            <Stack minWidth={900} gap={3}>
                <Stack>
                    <Stack paddingBottom={'0.2rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant='subtitle2' color={variable.greenLight}>
                            {t('stableSlopeAssessment')}
                        </Typography>
                        {user.data?.role !== ERole.Standard && (
                            <CheckboxCustom
                                checked={util.data?.stableSlopeAssessmentTable!}
                                label={t('printReport')}
                                handleChange={e => dispatch(setUtil({ ...util.data!, stableSlopeAssessmentTable: e.target.checked }))}
                            />
                        )}
                    </Stack>
                    <Table data={preInterventionSlopeValidationTable}></Table>
                    <Table data={postInterventionSlopeValidationTable}></Table>
                </Stack>
                <Stack>
                    <Stack paddingBottom={'0.2rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant='subtitle2' paddingBottom={'0.3rem'} color={variable.greenLight}>
                            {t('drainageCapacity')}
                        </Typography>
                        {user.data?.role !== ERole.Standard && (
                            <CheckboxCustom
                                checked={util.data?.drainageCapacityTable!}
                                label={t('printReport')}
                                handleChange={e => dispatch(setUtil({ ...util.data!, drainageCapacityTable: e.target.checked }))}
                            />
                        )}
                    </Stack>
                    <Table data={drainageCapacityTable}></Table>
                </Stack>
                {drainingDrainPipesTable && (
                    <Stack>
                        <Stack paddingBottom={'0.2rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <Typography variant='subtitle2' paddingBottom={'0.3rem'} color={variable.greenLight}>
                                {t('drainingDrainPipesCalculation')}
                            </Typography>
                            {user.data?.role !== ERole.Standard && (
                                <CheckboxCustom
                                    checked={util.data?.drainingDrainPipesCalculationTable!}
                                    label={t('printReport')}
                                    handleChange={e => dispatch(setUtil({ ...util.data!, drainingDrainPipesCalculationTable: e.target.checked }))}
                                />
                            )}
                        </Stack>
                        <Table data={drainingDrainPipesTable}></Table>
                    </Stack>
                )}
                <Stack>
                    <Stack paddingBottom={'0.2rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant='subtitle2' paddingBottom={'0.3rem'} color={variable.greenLight}>
                            {t('wheelbaseOutput')}
                        </Typography>
                        {user.data?.role !== ERole.Standard && (
                            <CheckboxCustom
                                checked={util.data?.wheelbaseOutputTable!}
                                label={t('printReport')}
                                handleChange={e => dispatch(setUtil({ ...util.data!, wheelbaseOutputTable: e.target.checked }))}
                            />
                        )}
                    </Stack>
                    <Table data={wheelbaseTable}></Table>
                </Stack>
                <Stack>
                    <Stack paddingBottom={'0.2rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant='subtitle2' paddingBottom={'0.3rem'} color={variable.greenLight}>
                            {t('transitionalRegime')}
                        </Typography>
                        {user.data?.role !== ERole.Standard && (
                            <CheckboxCustom
                                checked={util.data?.transitionalRegimeTable!}
                                label={t('printReport')}
                                handleChange={e => dispatch(setUtil({ ...util.data!, transitionalRegimeTable: e.target.checked }))}
                            />
                        )}
                    </Stack>
                    <Table data={transitionalRegimeTable}></Table>
                    <Table data={drainageSystemTable}></Table>
                </Stack>
                <Stack>
                    <Stack paddingBottom={'0.2rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant='subtitle2' paddingBottom={'0.3rem'} color={variable.greenLight}>
                            {t('pressuresInTheSoilCalculation')}
                        </Typography>
                        {user.data?.role !== ERole.Standard && (
                            <CheckboxCustom
                                checked={util.data?.pressuresInTheSoilCalculationTable!}
                                label={t('printReport')}
                                handleChange={e => dispatch(setUtil({ ...util.data!, pressuresInTheSoilCalculationTable: e.target.checked }))}
                            />
                        )}
                    </Stack>
                    <Table data={generalPressuresInTheSoilTable}></Table>
                    <Table data={longTermPressuresInTheSoilTable}></Table>
                    <Table data={shortTermPressuresInTheSoilTable}></Table>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default Output;
